/**
 * Gstr2bUploadAndEdit.jsx
 * Enhanced version — TransferErpData design system applied.
 * All original APIs, logic, and functionality preserved exactly.
 *
 * APIs used (unchanged):
 *   POST /api/entity/getAssignedEntity
 *   POST /api/gst2b/getGst2bUploadedLog
 *   POST /api/gst2b/uploadGst2b            (process_month as MON-YYYY string)
 *   POST /api/gst2b/getGst2bData
 *   PATCH /api/gst2b/update-rejected-records
 *   POST /api/gst2b/export                 (blob → file-saver)
 *   POST /api/gst2b/postGST2bFromStage
 *   POST /api/gst2b/deleteGST2bFromStage
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import axios      from "axios";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";
import { useNavigate }  from "react-router-dom";
import { selectUser }   from "../../features/userSlice";
import styles from "./TransferErpData.module.css";

/* ════════════════════════════════════════════════════════════
   HELPERS
════════════════════════════════════════════════════════════ */

/** Normalize Oracle/mixed DB values to plain strings */
const normalizeValue = (v) => {
  if (v === null || v === undefined) return "";
  if (v instanceof Date)             return v.toISOString();
  if (typeof v === "object")         return Object.values(v)[0] || "";
  return v;
};
const normalizeRows = (data) =>
  (data || []).map((row) => {
    const clean = {};
    Object.entries(row).forEach(([k, v]) => (clean[k] = normalizeValue(v)));
    return clean;
  });

/** Build last-12-months options as MON-YYYY strings */
const buildLast12Months = () => {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d   = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mon = d.toLocaleString("en-GB", { month: "short" }).toUpperCase().replace(".", "");
    const lbl = `${mon}-${d.getFullYear()}`;
    arr.push({ value: lbl, label: lbl });
  }
  return arr;
};

const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1048576)     return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(2)} MB`;
};

const fmtTimestamp = (val) => {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d)) return String(val);
    return d.toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
  } catch { return String(val); }
};

/* ════════════════════════════════════════════════════════════
   SUB-COMPONENTS  (same patterns as TransferErpData)
════════════════════════════════════════════════════════════ */

/* ── Collapsible Panel ── */
const Panel = ({ title, icon, open, onToggle, children, badge }) => (
  <div className={styles.panel}>
    <div className={styles.panelHeader} onClick={onToggle}>
      <div className={styles.panelHeaderLeft}>
        <span className={styles.panelToggle}>{open ? "▼" : "▶"}</span>
        {icon && <span style={{ marginRight: 5 }}>{icon}</span>}
        <span className={styles.panelTitle}>{title}</span>
        {badge != null && (
          <span style={{ fontSize:9, background:"#1b6ca8", color:"#fff", borderRadius:8, padding:"1px 6px", marginLeft:5 }}>
            {badge}
          </span>
        )}
      </div>
    </div>
    {open && <div className={styles.panelBody}>{children}</div>}
  </div>
);

/* ── Compact PagerBar ── */
const PagerBar = ({ page, totalPages, total, pageSize, onPage, onPageSize, pageSizeOptions = [5,10,15,25] }) => {
  if (totalPages === 0) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    const diff = Math.abs(i - page);
    if (i === 1 || i === totalPages || diff <= 1) pages.push(i);
    else if (diff === 2) pages.push("...");
  }
  const deduped = pages.filter((v, i, a) => v !== "..." || a[i - 1] !== "...");
  return (
    <div className={styles.pagination}>
      <button className={styles.pageBtn} onClick={() => onPage(1)} disabled={page === 1}>«</button>
      <button className={styles.pageBtn} onClick={() => onPage(page - 1)} disabled={page === 1}>‹</button>
      {deduped.map((n, i) =>
        n === "..." ? (
          <span key={`e${i}`} style={{ fontSize:10, color:"#888", padding:"0 2px" }}>…</span>
        ) : (
          <button key={n} className={`${styles.pageBtn} ${n === page ? styles.pageBtnActive : ""}`}
            onClick={() => onPage(n)}>{n}</button>
        )
      )}
      <button className={styles.pageBtn} onClick={() => onPage(page + 1)} disabled={page === totalPages}>›</button>
      <button className={styles.pageBtn} onClick={() => onPage(totalPages)} disabled={page === totalPages}>»</button>
      <span className={styles.pageInfo}>
        Page {page}/{totalPages} &nbsp;|&nbsp; {total.toLocaleString("en-IN")} records &nbsp;|&nbsp;
        <select className={styles.select}
          style={{ height:20, fontSize:10, minWidth:48, marginLeft:4 }}
          value={pageSize}
          onChange={e => onPageSize(parseInt(e.target.value))}>
          {pageSizeOptions.map(s => <option key={s} value={s}>{s}/pg</option>)}
        </select>
      </span>
    </div>
  );
};

/* ── POSTED_FLAG status badge ── */
const PostedBadge = ({ flag }) =>
  flag === "Y" ? (
    <span style={{ display:"inline-block", padding:"1px 8px", borderRadius:9, fontSize:10, fontWeight:600,
                   background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" }}>✓ Posted</span>
  ) : (
    <span style={{ display:"inline-block", padding:"1px 8px", borderRadius:9, fontSize:10, fontWeight:600,
                   background:"#fef3c7", color:"#92400e", border:"1px solid #fcd34d" }}>⏳ Pending</span>
  );

/* ── Entity Chip ── */
const EntityChip = ({ code }) => (
  <span className={code === "SE" ? styles.chipSe : styles.chipSs}>{code || "—"}</span>
);

/* ── Inline message banner ── */
const MsgBanner = ({ type, text, onClose }) => {
  if (!text) return null;
  const cls  = type === "success" ? styles.msgSuccess
             : type === "danger"  ? styles.msgError
             : type === "warning" ? styles.msgWarn
             : styles.msgInfo;
  const icon = type === "success" ? "✓" : type === "danger" ? "✕" : type === "warning" ? "⚠" : "ℹ";
  return (
    <div className={`${styles.msg} ${cls}`} style={{ marginTop:8 }}>
      <span className={styles.msgIcon}>{icon}</span>
      {text}
      {onClose && (
        <button onClick={onClose}
          style={{ marginLeft:"auto", background:"none", border:"none", cursor:"pointer", fontSize:11, opacity:0.6 }}>✕</button>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const Gstr2bUploadAndEdit = () => {
  const user     = useSelector(selectUser);
  const navigate = useNavigate();
  const token    = user?.token;
  const emp_id   = user?.emp_id;

  /* ── Panel open state ── */
  const [uploadOpen,   setUploadOpen]   = useState(true);
  const [logOpen,      setLogOpen]      = useState(true);
  const [dataGridOpen, setDataGridOpen] = useState(true);
  const [editOpen,     setEditOpen]     = useState(true);

  /* ── Entity ── */
  const [entityCode,    setEntityCode]    = useState("");
  const [entityOptions, setEntityOptions] = useState([]);

  /* ── Upload form ── */
  const [file,                 setFile]                 = useState(null);
  const [selectedProcessMonth, setSelectedProcessMonth] = useState(null);
  const [processMonthOptions,  setProcessMonthOptions]  = useState([]);
  const [uploadLoading,        setUploadLoading]        = useState(false);
  const [lastUploadStat,       setLastUploadStat]       = useState(null); // for stat strip

  /* ── Upload Log ── */
  const [uploadLog,      setUploadLog]      = useState([]);
  const [logPage,        setLogPage]        = useState(1);
  const [logRowsPerPage, setLogRowsPerPage] = useState(5);
  const [postingRow,     setPostingRow]     = useState(null); // BATCH_ID being posted/deleted

  /* ── Data Grid (loaded per batch) ── */
  const [rows,        setRows]        = useState([]);
  const [currentBatch,setCurrentBatch]= useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  /* ── Data Grid controls ── */
  const [searchField, setSearchField] = useState("");
  const [filterText,  setFilterText]  = useState("");
  const [sortField,   setSortField]   = useState(null);
  const [sortDir,     setSortDir]     = useState("asc");
  const [page,        setPage]        = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  /* ── Inline Edit ── */
  const [editedRow, setEditedRow] = useState(null);

  /* ── Message ── */
  const [message,     setMessage]     = useState("");
  const [messageType, setMessageType] = useState("info");
  const clearMsg = () => setMessage("");

  /* ═══════════════════════════════════
     LOAD ENTITIES
  ═══════════════════════════════════ */
  const fetchEntities = async () => {
    try {
      const res = await axios.post(
        "/api/entity/getAssignedEntity",
        { emp_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = res.data || [];
      setEntityOptions(list);
      if (!entityCode && list.length > 0) {
        setEntityCode(list[0].ENTITY_CODE); // auto-select first
      }
    } catch (err) {
      console.error(err);
      setMessageType("danger");
      setMessage("Failed to load entities.");
    }
  };

  /* ═══════════════════════════════════
     LOAD UPLOAD LOG
  ═══════════════════════════════════ */
  const fetchUploadLog = async () => {
    try {
      const res = await axios.post(
        "/api/gst2b/getGst2bUploadedLog",
        { entity_code: entityCode, emp_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUploadLog(res.data?.data || []);
    } catch (err) {
      console.error(err);
      setMessageType("danger");
      setMessage("Failed to load upload log.");
    }
  };

  /* ═══════════════════════════════════
     EFFECTS
  ═══════════════════════════════════ */
  useEffect(() => {
    if (!token) return;
    const opts = buildLast12Months();
    setProcessMonthOptions(opts);
    setSelectedProcessMonth(opts[0]);
    fetchEntities();
  }, [token]); // eslint-disable-line

  useEffect(() => {
    if (!token || !entityCode) return;
    fetchUploadLog();
  }, [token, entityCode]); // eslint-disable-line

  /* ═══════════════════════════════════
     UPLOAD FILE
  ═══════════════════════════════════ */
  const handleUpload = async () => {
    if (!entityCode) {
      setMessageType("warning"); setMessage("Please select Entity."); return;
    }
    if (!file) {
      setMessageType("warning"); setMessage("Please select a file."); return;
    }
    if (!selectedProcessMonth?.value) {
      setMessageType("warning"); setMessage("Please select Process Month."); return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("process_month", selectedProcessMonth.value); // MON-YYYY — sent as-is
    formData.append("entity_code", entityCode);

    setUploadLoading(true);
    setMessage("Uploading…"); setMessageType("info");
    setLastUploadStat(null);

    try {
      const res = await axios.post("/api/gst2b/uploadGst2b", formData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10 * 60 * 1000,
      });
      setMessageType("success");
      setMessage(res.data.message || "Upload successful.");
      setLastUploadStat({
        entity:  entityCode,
        period:  selectedProcessMonth.value,
        file:    file.name,
        count:   res.data.inserted ?? null,
      });
      setFile(null);
      fetchUploadLog();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || err.response?.data?.message || "Upload failed.";
      setMessageType("danger"); setMessage(msg);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleClearUpload = () => {
    setFile(null);
    setMessage(""); setLastUploadStat(null);
  };

  /* ═══════════════════════════════════
     VIEW DATA FOR BATCH
  ═══════════════════════════════════ */
  const fetchDataForBatch = async (batch) => {
    if (!batch) return;
    setDataLoading(true);
    setRows([]); setEditedRow(null);
    setSearchField(""); setFilterText(""); setSortField(null); setPage(1);
    setDataGridOpen(true);

    try {
      const res = await axios.post(
        "/api/gst2b/getGst2bData",
        { entity_code: entityCode, period: batch.GSTR2B_PERIOD, batch_id: batch.BATCH_ID, emp_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(normalizeRows(res.data || []));
      setCurrentBatch(batch);
      setMessageType("success");
      setMessage(`Loaded ${res.data.length} rows for Batch ${batch.BATCH_ID}.`);
    } catch (err) {
      console.error(err);
      setMessageType("danger"); setMessage("Failed to load data.");
    } finally {
      setDataLoading(false);
    }
  };

  /* ═══════════════════════════════════
     SAVE CORRECTIONS (inline edit)
  ═══════════════════════════════════ */
  const handleSave = async () => {
    if (!editedRow) return;
    try {
      await axios.patch(
        "/api/gst2b/update-rejected-records",
        { records: [editedRow] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessageType("success"); setMessage("Saved successfully.");
      if (currentBatch) fetchDataForBatch(currentBatch);
      setEditedRow(null);
    } catch (err) {
      console.error(err);
      setMessageType("danger"); setMessage("Save failed.");
    }
  };

  /* ═══════════════════════════════════
     EXPORT XLSX (server blob)
  ═══════════════════════════════════ */
  const handleExportXLSX = async () => {
    if (!currentBatch) {
      setMessageType("warning"); setMessage("Load a batch first."); return;
    }
    try {
      const res = await axios.post(
        "/api/gst2b/export",
        { batch_id: currentBatch.BATCH_ID, period: currentBatch.GSTR2B_PERIOD, emp_id, entity_code: entityCode },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      saveAs(new Blob([res.data]), `gst2b_${currentBatch.GSTR2B_PERIOD}_${currentBatch.BATCH_ID}.xlsx`);
      setMessageType("success"); setMessage("Exported successfully.");
    } catch (err) {
      console.error(err);
      setMessageType("danger"); setMessage("Export failed.");
    }
  };

  /* ═══════════════════════════════════
     POST BATCH
  ═══════════════════════════════════ */
  const postGST2b = async (batch) => {
    setPostingRow(batch.BATCH_ID);
    try {
      const res = await axios.post(
        "/api/gst2b/postGST2bFromStage",
        {
          entity_code: entityCode,
          batch_no:    batch.BATCH_ID,
          period:      batch.GSTR2B_PERIOD,
          file_name:   batch.FILE_NAME,
          fy:          batch.GSTR2B_PERIOD,
          posted_by:   batch.UPLOADED_BY,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessageType("success"); setMessage(res.data.message);
      fetchUploadLog();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || err.response?.data?.error || "Posting failed.";
      setMessageType("danger"); setMessage(msg);
    } finally {
      setPostingRow(null);
    }
  };

  /* ═══════════════════════════════════
     DELETE BATCH
  ═══════════════════════════════════ */
  const deleteGST2b = async (batch) => {
    setPostingRow(batch.BATCH_ID);
    try {
      const res = await axios.post(
        "/api/gst2b/deleteGST2bFromStage",
        {
          entity_code: entityCode,
          batch_no:    batch.BATCH_ID,
          period:      batch.GSTR2B_PERIOD,
          file_name:   batch.FILE_NAME,
          fy:          batch.GSTR2B_PERIOD,
          posted_by:   batch.UPLOADED_BY,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessageType("success"); setMessage(res.data.message);
      fetchUploadLog();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || err.response?.data?.error || "Delete failed.";
      setMessageType("danger"); setMessage(msg);
    } finally {
      setPostingRow(null);
    }
  };

  /* ═══════════════════════════════════
     DATA GRID — FILTER / SORT / PAGE
     (dynamic columns — kept as-is)
  ═══════════════════════════════════ */
  const filtered = useMemo(() =>
    rows.filter(row =>
      searchField && filterText
        ? String(row[searchField] || "").toLowerCase().includes(filterText.toLowerCase())
        : true
    ),
    [rows, searchField, filterText]
  );

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const av = String(a[sortField] ?? "");
      const bv = String(b[sortField] ?? "");
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filtered, sortField, sortDir]);

  const totalPages    = Math.ceil(sorted.length / rowsPerPage) || 1;
  const paginatedRows = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  /* ── Log pagination ── */
  const logTotalPages  = Math.ceil(uploadLog.length / logRowsPerPage) || 1;
  const paginatedLog   = uploadLog.slice((logPage - 1) * logRowsPerPage, logPage * logRowsPerPage);

  const handleColSort = (col) => {
    if (sortField === col) { setSortDir(d => d === "asc" ? "desc" : "asc"); }
    else { setSortField(col); setSortDir("asc"); }
    setPage(1);
  };

  /* ── Column keys for dynamic grid ── */
  const colKeys = rows[0] ? Object.keys(rows[0]) : [];

  /* ═══════════════════════════════════
     AUTH GUARD  — must be AFTER all hooks
  ═══════════════════════════════════ */
  if (!token) {
    return (
      <div className={styles.root}>
        <div className={styles.loginWall}>
          <div>You must be logged in to access GSTR-2B Upload.</div>
          <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => navigate("/")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <div className={styles.root}>

      {/* ── Shell Bar ── */}
      <div className={styles.shell}>
        <div className={styles.shellLogo}>GST Portal</div>
        <div className={styles.shellTitle}>GSTR-2B Reconciliation System</div>
        <div className={styles.shellRight}>
          <span>User: {user?.name || user?.email || "—"}</span>
          <span>|</span>
          <span>{new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</span>
        </div>
      </div>

      <div className={styles.page}>

        {/* ── Page Title Bar ── */}
        <div className={styles.titleBar}>
          <h1>GSTR-2B Upload &amp; Edit</h1>
          <div className={styles.breadcrumb}>
            <span>Home</span> &rsaquo; GST Modules &rsaquo; <span>GSTR-2B</span> &rsaquo; Upload &amp; Edit
          </div>
        </div>

        {/* ── Global Message Banner ── */}
        <MsgBanner type={messageType} text={message} onClose={clearMsg} />

        {/* ══════════════════════════════════════════
            PANEL 1 — UPLOAD
        ══════════════════════════════════════════ */}
        <Panel title="Upload GSTR-2B File" icon="📤" open={uploadOpen} onToggle={() => setUploadOpen(o => !o)}>

          {/* Toolbar */}
          <div className={styles.toolbar}>
            <button className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={uploadLoading} onClick={handleUpload}>
              {uploadLoading
                ? <><span className={styles.spinner} style={{ width:11, height:11, borderWidth:2, marginRight:5 }}/>Uploading…</>
                : "▶ Upload File"}
            </button>
            <div className={styles.toolbarSep}/>
            <button className={`${styles.btn} ${styles.btnDefault}`}
              disabled={uploadLoading} onClick={handleClearUpload}>
              ✕ Clear
            </button>
          </div>

          {/* Form Fields */}
          <div className={styles.formRow}>

            {/* Entity */}
            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelReq}`}>Entity</label>
              <select className={styles.select} style={{ minWidth:260 }}
                value={entityCode} disabled={uploadLoading}
                onChange={e => setEntityCode(e.target.value)}>
                <option value="">-- Select Entity --</option>
                {entityOptions.map(e => (
                  <option key={e.ENTITY_CODE} value={e.ENTITY_CODE}>
                    {e.ENTITY_CODE} — {e.ENTITY_NAME}
                  </option>
                ))}
              </select>
            </div>

            {/* Process Month — single dropdown (MON-YYYY, last 12) */}
            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelReq}`}>Process Month</label>
              <select className={styles.select} style={{ minWidth:130 }}
                value={selectedProcessMonth?.value || ""}
                disabled={uploadLoading}
                onChange={e =>
                  setSelectedProcessMonth(processMonthOptions.find(m => m.value === e.target.value))
                }>
                {processMonthOptions.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* File Picker with preview */}
            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelReq}`}>Excel File (.xlsx / .xls)</label>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <label style={{
                  display:"inline-flex", alignItems:"center", gap:6,
                  padding:"3px 10px", borderRadius:3, fontSize:11,
                  border:"1px solid #c5cdd6",
                  background: uploadLoading ? "#f4f4f4" : "#fff",
                  cursor: uploadLoading ? "not-allowed" : "pointer",
                  color:"#374151", whiteSpace:"nowrap",
                }}>
                  📂 Choose File
                  <input type="file" accept=".xlsx,.xls" style={{ display:"none" }}
                    disabled={uploadLoading}
                    onChange={e => setFile(e.target.files[0] || null)}/>
                </label>

                {file ? (
                  <div style={{
                    display:"flex", alignItems:"center", gap:6,
                    background:"#eaf2fa", borderRadius:4, padding:"3px 8px",
                    border:"1px solid #bfdbfe", maxWidth:280,
                  }}>
                    <span style={{ fontSize:14 }}>📊</span>
                    <div style={{ overflow:"hidden" }}>
                      <div style={{ fontSize:11, fontWeight:600, color:"#1b4f72",
                                    whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:200 }}>
                        {file.name}
                      </div>
                      <div style={{ fontSize:10, color:"#6b7280" }}>{formatBytes(file.size)}</div>
                    </div>
                    <button onClick={() => setFile(null)}
                      style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:"#6b7280", padding:"0 2px" }}
                      title="Remove">✕</button>
                  </div>
                ) : (
                  <span style={{ fontSize:11, color:"#9ca3af", fontStyle:"italic" }}>No file chosen</span>
                )}
              </div>
            </div>

          </div>{/* /formRow */}

          {/* Uploading spinner */}
          {uploadLoading && (
            <div className={styles.processing}>
              <div className={styles.spinner}/>
              <span>Uploading and processing GSTR-2B file… please wait.</span>
            </div>
          )}

          {/* Stat Strip — appears after successful upload */}
          {lastUploadStat && (
            <div className={styles.statStrip} style={{ marginTop:10 }}>
              {lastUploadStat.count != null && (
                <div className={styles.statItem}>
                  <span className={styles.statLbl}>Records Inserted</span>
                  <span className={styles.statVal}>{Number(lastUploadStat.count).toLocaleString("en-IN")}</span>
                </div>
              )}
              <div className={styles.statItem}>
                <span className={styles.statLbl}>Entity</span>
                <span className={styles.statVal} style={{ fontSize:13 }}>{lastUploadStat.entity}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLbl}>Period</span>
                <span className={styles.statVal} style={{ fontSize:13 }}>{lastUploadStat.period}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLbl}>File</span>
                <span className={styles.statVal} style={{
                  fontSize:11, maxWidth:220,
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                }}>{lastUploadStat.file}</span>
              </div>
            </div>
          )}

        </Panel>

        {/* ══════════════════════════════════════════
            PANEL 2 — UPLOAD LOG
        ══════════════════════════════════════════ */}
        <Panel
          title="Uploaded Files Log"
          icon="📋"
          open={logOpen}
          onToggle={() => setLogOpen(o => !o)}
          badge={uploadLog.length > 0 ? uploadLog.length : null}
        >
          {/* Log Toolbar */}
          <div className={styles.toolbar}>
            <button className={`${styles.btn} ${styles.btnDefault}`} onClick={fetchUploadLog}>
              ↺ Refresh
            </button>
            <div className={styles.toolbarSep}/>
            <span className={styles.tableInfo} style={{ margin:0 }}>
              {uploadLog.length} records total
            </span>
          </div>

          {/* Log Table */}
          <div className={styles.tableWrap}>
            <table className={styles.table} style={{ minWidth:940 }}>
              <thead>
                <tr>
                  {["Actions","#","Uploaded On","Uploaded By","File Name","Batch ID","Period","Process Month","Entity","Status"].map((h,i) => (
                    <th key={i} style={{
                      textAlign: i===0||i===1 ? "center" : "left",
                      fontSize:10, fontWeight:"bold",
                      padding:"4px 8px",
                      background:"#eaf0f6",
                      borderBottom:"2px solid #1b6ca8",
                      whiteSpace:"nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedLog.length === 0 ? (
                  <tr>
                    <td colSpan={10}>
                      <div className={styles.emptyState}>No uploaded files found.</div>
                    </td>
                  </tr>
                ) : (
                  paginatedLog.map((r, idx) => {
                    const isBusy = postingRow === r.BATCH_ID;
                    return (
                      <tr key={r.ID} className={idx % 2 !== 0 ? styles.rowAlt : ""}>

                        {/* Actions cell */}
                        <td style={{ padding:"3px 8px", whiteSpace:"nowrap", textAlign:"center" }}>
                          {/* View */}
                          <button
                            className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
                            style={{ marginRight:4 }}
                            disabled={isBusy || dataLoading}
                            onClick={() => fetchDataForBatch(r)}
                            title="Load this batch into the data grid"
                          >
                            {dataLoading && currentBatch?.BATCH_ID === r.BATCH_ID
                              ? <><span className={styles.spinner} style={{ width:9, height:9, borderWidth:1.5, marginRight:3 }}/>Loading</>
                              : "👁 View"}
                          </button>

                          {/* Post */}
                          <button
                            className={`${styles.btn} ${styles.btnSm} ${r.POSTED_FLAG === "Y" ? styles.btnDefault : styles.btnPrimary}`}
                            style={{ marginRight:4 }}
                            disabled={r.POSTED_FLAG === "Y" || isBusy}
                            onClick={() => postGST2b(r)}
                            title={r.POSTED_FLAG === "Y" ? "Already posted" : "Post this batch"}
                          >
                            {isBusy && postingRow === r.BATCH_ID
                              ? <><span className={styles.spinner} style={{ width:9, height:9, borderWidth:1.5, marginRight:3 }}/>Wait</>
                              : r.POSTED_FLAG === "Y" ? "✓ Posted" : "▶ Post"}
                          </button>

                          {/* Delete */}
                          <button
                            className={`${styles.btn} ${styles.btnSm}`}
                            style={{
                              background:"#fef2f2", color:"#b91c1c",
                              border:"1px solid #fca5a5",
                            }}
                            disabled={isBusy}
                            onClick={() => {
                              if (window.confirm(`Delete batch ${r.BATCH_ID}?`)) deleteGST2b(r);
                            }}
                            title="Delete this batch"
                          >
                            🗑 Delete
                          </button>
                        </td>

                        <td style={{ textAlign:"center", padding:"3px 8px", fontSize:11, color:"#6b7280" }}>
                          {(logPage - 1) * logRowsPerPage + idx + 1}
                        </td>
                        <td style={{ padding:"3px 8px", fontSize:11, whiteSpace:"nowrap" }}>
                          {fmtTimestamp(r.UPLOAD_TIMESTAMP)}
                        </td>
                        <td style={{ padding:"3px 8px", fontSize:11, color:"#374151" }}>
                          {r.UPLOADED_BY || "—"}
                        </td>
                        <td style={{ padding:"3px 8px", fontSize:11, maxWidth:220,
                                     overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                                     color:"#0d3a6a" }}
                            title={r.FILE_NAME}>
                          📊 {r.FILE_NAME || "—"}
                        </td>
                        <td style={{ padding:"3px 8px", fontSize:11, fontFamily:"monospace", color:"#6b7280" }}>
                          {r.BATCH_ID || "—"}
                        </td>
                        <td style={{ padding:"3px 8px", fontSize:11, fontWeight:600, color:"#1b4f72" }}>
                          {r.GSTR2B_PERIOD || "—"}
                        </td>
                        <td style={{ padding:"3px 8px", fontSize:11 }}>
                          <span className={styles.periodText}>{r.PROCESS_MONTH || "—"}</span>
                        </td>
                        <td style={{ padding:"3px 8px", fontSize:11 }}>
                          <EntityChip code={r.ENTITY_CODE}/>
                        </td>
                        <td style={{ padding:"3px 8px" }}>
                          <PostedBadge flag={r.POSTED_FLAG}/>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <PagerBar
            page={logPage} totalPages={logTotalPages}
            total={uploadLog.length} pageSize={logRowsPerPage}
            onPage={setLogPage}
            onPageSize={n => { setLogRowsPerPage(n); setLogPage(1); }}
            pageSizeOptions={[5, 10, 15, 25]}
          />
        </Panel>

        {/* ══════════════════════════════════════════
            PANEL 3 — DATA GRID (batch viewer)
        ══════════════════════════════════════════ */}
        <Panel
          title={currentBatch
            ? `Data Grid — Batch ${currentBatch.BATCH_ID} · ${currentBatch.GSTR2B_PERIOD}`
            : "Data Grid — Select a Batch from Upload Log"}
          icon="📑"
          open={dataGridOpen}
          onToggle={() => setDataGridOpen(o => !o)}
          badge={sorted.length > 0 ? `${sorted.length.toLocaleString("en-IN")} records` : null}
        >
          {/* Data Grid Toolbar */}
          <div className={styles.toolbar} style={{ flexWrap:"wrap" }}>

            {/* Search field select */}
            <select className={styles.select} style={{ minWidth:140 }}
              value={searchField}
              onChange={e => { setSearchField(e.target.value); setPage(1); }}>
              <option value="">— Search Field —</option>
              {colKeys.map((f, i) => <option key={i} value={f}>{f}</option>)}
            </select>

            {/* Search text */}
            <input
              type="text"
              placeholder="Search text…"
              className={styles.input}
              style={{ height:26, fontSize:11, width:200 }}
              value={filterText}
              onChange={e => { setFilterText(e.target.value); setPage(1); }}
            />

            <div className={styles.toolbarSep}/>

            {/* Save */}
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}
              onClick={handleSave} disabled={!editedRow}
              title={editedRow ? "Save edited row" : "Select a row to edit first"}>
              💾 Save
            </button>

            {/* Export */}
            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
              onClick={handleExportXLSX} disabled={!currentBatch}>
              📥 Export XLSX
            </button>

            {/* Clear search */}
            {(searchField || filterText) && (
              <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
                onClick={() => { setSearchField(""); setFilterText(""); setPage(1); }}>
                ✕ Clear Search
              </button>
            )}

            <div className={styles.toolbarSep}/>

            {/* Record count */}
            <span className={styles.tableInfo} style={{ margin:0 }}>
              {filtered.length !== rows.length
                ? <><strong>{filtered.length}</strong> / {rows.length} records</>
                : <><strong>{rows.length}</strong> records</>
              }
            </span>
          </div>

          {/* Loading */}
          {dataLoading && (
            <div className={styles.processing}>
              <div className={styles.spinner}/>
              <span>Loading batch data…</span>
            </div>
          )}

          {/* Table */}
          <div className={styles.detailTableWrap}>
            <table className={styles.detailTable}>
              <thead>
                <tr>
                  {/* Action column */}
                  <th style={{
                    width:46, background:"#eaf0f6", fontSize:10, fontWeight:"bold",
                    padding:"4px 6px", borderBottom:"2px solid #1b6ca8",
                    position:"sticky", top:0, zIndex:2, textAlign:"center",
                  }}>Edit</th>

                  {/* Dynamic columns from API response */}
                  {colKeys.map((col, idx) => (
                    <th key={idx}
                      className={styles.filterTh}
                      style={{
                        cursor:"pointer", userSelect:"none",
                        background: sortField === col ? "#dce8f5" : "#eaf0f6",
                      }}
                      onClick={() => handleColSort(col)}
                      title="Click to sort"
                    >
                      <div className={styles.filterThInner}>
                        <span className={styles.filterThLabel}>{col}</span>
                        {sortField === col && (
                          <span className={styles.sortIndicator}>
                            {sortDir === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {paginatedRows.length === 0 && !dataLoading ? (
                  <tr>
                    <td colSpan={colKeys.length + 1}>
                      <div className={styles.emptyState}>
                        {rows.length === 0
                          ? "No data loaded. Click 👁 View from the Upload Log above."
                          : "No records match the current search."}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, idx) => {
                    const isEditing = editedRow && Object.entries(row).every(([k, v]) => editedRow[k] === v
                      || (editedRow[k] !== undefined)); // highlight if this row is being edited
                    return (
                      <tr key={idx}
                        className={editedRow && JSON.stringify(editedRow) === JSON.stringify(row)
                          ? styles.rowSelected : idx % 2 !== 0 ? styles.rowAlt : ""}>
                        {/* Edit button */}
                        <td style={{ textAlign:"center", padding:"2px 4px" }}>
                          <button
                            className={`${styles.btn} ${styles.btnSm} ${styles.btnDefault}`}
                            style={{ padding:"1px 6px" }}
                            onClick={() => { setEditedRow({ ...row }); setEditOpen(true); }}
                            title="Edit this row"
                          >✏</button>
                        </td>
                        {/* Dynamic data cells */}
                        {Object.entries(row).map(([field, value]) => (
                          <td key={field} title={String(value)}
                            style={{ padding:"2px 6px", fontSize:11, whiteSpace:"nowrap",
                                     maxWidth:200, overflow:"hidden", textOverflow:"ellipsis" }}>
                            {value}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Data Grid Pagination */}
          {rows.length > 0 && (
            <PagerBar
              page={page} totalPages={totalPages}
              total={sorted.length} pageSize={rowsPerPage}
              onPage={p => setPage(p)}
              onPageSize={n => { setRowsPerPage(n); setPage(1); }}
              pageSizeOptions={[10, 15, 20, 50, 100]}
            />
          )}
        </Panel>

        {/* ══════════════════════════════════════════
            PANEL 4 — INLINE EDIT FORM
        ══════════════════════════════════════════ */}
        {editedRow && (
          <Panel
            title="Edit Row"
            icon="✏"
            open={editOpen}
            onToggle={() => setEditOpen(o => !o)}
          >
            {/* Edit toolbar */}
            <div className={styles.toolbar}>
              <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSave}>
                💾 Save Changes
              </button>
              <div className={styles.toolbarSep}/>
              <button className={`${styles.btn} ${styles.btnDefault}`}
                onClick={() => setEditedRow(null)}>
                ✕ Cancel
              </button>
            </div>

            {/* Edit fields — 3-column grid */}
            <div style={{
              display:"grid",
              gridTemplateColumns:"repeat(3, 1fr)",
              gap:"8px 16px",
              padding:"4px 0",
            }}>
              {Object.entries(editedRow).map(([field, value], i) => (
                <div key={i}>
                  <label style={{
                    display:"block", fontSize:10, fontWeight:600,
                    color:"#4b5563", marginBottom:2,
                  }}>{field}</label>
                  <input
                    className={styles.input}
                    style={{ width:"100%", height:26, fontSize:11 }}
                    value={value || ""}
                    onChange={e => setEditedRow({ ...editedRow, [field]: e.target.value })}
                  />
                </div>
              ))}
            </div>

          </Panel>
        )}

      </div>{/* /page */}
    </div>
  );
};

export default Gstr2bUploadAndEdit;
